// Programa Vecinos / Referidos.
// Para cada portero (vecino): cuántas suscripciones tiene como referido,
// ingresos generados, comisiones devengadas y pendientes de pago.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`

export default function Vecinos() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [porteros, setPorteros] = useState([])
  const [clientes, setClientes] = useState([])
  const [pagos, setPagos] = useState([])
  const [comisiones, setComisiones] = useState([])

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const [
        { data: po, error: pe },
        { data: cl, error: ce },
        { data: pa, error: pae },
        { data: co, error: coe },
      ] = await Promise.all([
        supabase.from('porteros').select(`
          id, slug, lugar, activo, direccion, edificio_nombre, cant_unidades, barrio_id, created_at,
          profile:profiles!porteros_id_fkey(nombre, phone, email),
          barrio:barrios(nombre)
        `),
        supabase.from('clientes').select('id, estado, portero_referido_id, fecha_inicio, direccion, barrio_id, cancelled_at'),
        supabase.from('pagos').select('cliente_id, monto').eq('estado', 'pagado'),
        supabase.from('comisiones').select('id, portero_id, monto_total, estado, mes, hold_hasta, hold_motivo'),
      ])
      if (pe) throw pe
      if (ce) throw ce
      if (pae) throw pae
      if (coe) throw coe
      setPorteros(po || [])
      setClientes(cl || [])
      setPagos(pa || [])
      setComisiones(co || [])
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  // Normaliza una dirección para comparar (quita tildes, lowercase, solo letras+números)
  function normalizar(d) {
    return (d || '').toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]/g, ' ')
      .replace(/\s+/g, ' ').trim()
  }

  // % similitud entre 2 direcciones (Jaccard sobre tokens)
  function similitud(a, b) {
    const tokA = new Set(normalizar(a).split(' ').filter(t => t.length > 2))
    const tokB = new Set(normalizar(b).split(' ').filter(t => t.length > 2))
    if (tokA.size === 0 || tokB.size === 0) return 0
    let inter = 0
    tokA.forEach(t => { if (tokB.has(t)) inter += 1 })
    const union = tokA.size + tokB.size - inter
    return union > 0 ? inter / union : 0
  }

  // Agregados por portero (con señales antifraude)
  const filas = useMemo(() => {
    const subsPorPort = new Map()
    clientes.forEach(c => {
      if (!c.portero_referido_id) return
      if (!subsPorPort.has(c.portero_referido_id)) {
        subsPorPort.set(c.portero_referido_id, {
          total: 0, activos: 0, cancelados: 0, ids: [], direcciones: [], barrios: []
        })
      }
      const r = subsPorPort.get(c.portero_referido_id)
      r.total += 1
      r.ids.push(c.id)
      r.direcciones.push(c.direccion)
      r.barrios.push(c.barrio_id)
      if (c.estado === 'activa') r.activos += 1
      if (c.estado === 'cancelada') r.cancelados += 1
    })

    const pagosPorCli = new Map()
    pagos.forEach(p => pagosPorCli.set(p.cliente_id, (pagosPorCli.get(p.cliente_id) || 0) + Number(p.monto || 0)))

    const comisPorPort = new Map()
    comisiones.forEach(co => {
      if (!comisPorPort.has(co.portero_id)) comisPorPort.set(co.portero_id, { total: 0, pagado: 0, pendiente: 0, hold: 0 })
      const r = comisPorPort.get(co.portero_id)
      const monto = Number(co.monto_total || 0)
      r.total += monto
      if (co.estado === 'pagada') r.pagado += monto
      else if (co.estado === 'hold_verificacion') r.hold += monto
      else r.pendiente += monto
    })

    return porteros.map(p => {
      const subs = subsPorPort.get(p.id) || { total: 0, activos: 0, cancelados: 0, ids: [], direcciones: [], barrios: [] }
      const ingresoGenerado = subs.ids.reduce((s, id) => s + (pagosPorCli.get(id) || 0), 0)
      const comis = comisPorPort.get(p.id) || { total: 0, pagado: 0, pendiente: 0 }

      // ── ANTIFRAUDE: señales calculadas
      // 1. % penetración: referidos vs apartamentos del edificio
      const penetracion_pct = (p.cant_unidades && subs.activos > 0)
        ? (subs.activos / p.cant_unidades) * 100 : null

      // 2. % match dirección: cuántos vecinos viven en una dirección parecida al edificio
      const matchesDir = subs.direcciones.filter(d => similitud(d, p.direccion) >= 0.5).length
      const match_direccion_pct = subs.direcciones.length > 0
        ? (matchesDir / subs.direcciones.length) * 100 : null

      // 3. % match barrio: cuántos referidos están en el mismo barrio del portero
      const matchesBarrio = subs.barrios.filter(b => b && b === p.barrio_id).length
      const match_barrio_pct = subs.barrios.length > 0
        ? (matchesBarrio / subs.barrios.length) * 100 : null

      // 4. % cancelación
      const cancel_pct = subs.total > 0 ? (subs.cancelados / subs.total) * 100 : 0

      // 5. score de riesgo (0-100, mayor = más sospechoso)
      let riesgo = 0
      const motivos_riesgo = []
      if (penetracion_pct !== null && penetracion_pct > 90) {
        riesgo += 40
        motivos_riesgo.push(`Penetración ${penetracion_pct.toFixed(0)}% (>90%)`)
      } else if (penetracion_pct !== null && penetracion_pct > 70) {
        riesgo += 20
        motivos_riesgo.push(`Penetración alta ${penetracion_pct.toFixed(0)}%`)
      }
      if (match_direccion_pct !== null && match_direccion_pct < 50 && subs.activos >= 3) {
        riesgo += 30
        motivos_riesgo.push(`Solo ${match_direccion_pct.toFixed(0)}% de vecinos viven en el edificio`)
      }
      if (match_barrio_pct !== null && match_barrio_pct < 60 && subs.activos >= 3) {
        riesgo += 15
        motivos_riesgo.push(`Solo ${match_barrio_pct.toFixed(0)}% en el mismo barrio`)
      }
      if (cancel_pct > 50 && subs.total >= 4) {
        riesgo += 15
        motivos_riesgo.push(`Cancelación alta: ${cancel_pct.toFixed(0)}%`)
      }
      if (!p.cant_unidades) motivos_riesgo.push('Falta cant_unidades en alta')

      return {
        id: p.id,
        nombre: p.profile?.nombre || '—',
        phone: p.profile?.phone,
        email: p.profile?.email,
        lugar: p.lugar,
        direccion: p.direccion,
        edificio_nombre: p.edificio_nombre,
        cant_unidades: p.cant_unidades,
        barrio: p.barrio?.nombre,
        activo: p.activo,
        subs_total: subs.total,
        subs_activos: subs.activos,
        subs_cancelados: subs.cancelados,
        ingreso_generado: ingresoGenerado,
        comision_total: comis.total,
        comision_pagada: comis.pagado,
        comision_pendiente: comis.pendiente,
        // antifraude
        penetracion_pct,
        match_direccion_pct,
        match_barrio_pct,
        cancel_pct,
        riesgo,
        motivos_riesgo,
      }
    }).sort((a, b) => b.ingreso_generado - a.ingreso_generado)
  }, [porteros, clientes, pagos, comisiones])

  const totales = useMemo(() => {
    const t = { porteros: filas.length, activos: 0, subs_referidos: 0, ingreso: 0, comision_pendiente: 0, comision_pagada: 0, alto_riesgo: 0 }
    filas.forEach(r => {
      if (r.activo) t.activos += 1
      t.subs_referidos += r.subs_activos
      t.ingreso += r.ingreso_generado
      t.comision_pendiente += r.comision_pendiente
      t.comision_pagada += r.comision_pagada
      if (r.riesgo >= 50) t.alto_riesgo += 1
    })
    return t
  }, [filas])

  return (
    <AdminLayout>
      <div className="page">
        <h1>🏢 Programa Vecinos / Referidos</h1>
        <p className="muted">Performance del programa de referidos. Cada portero/vecino gana comisión por suscriptores que trae.</p>

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Vecinos en programa</h3>
            <div className="stat">{totales.porteros}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{totales.activos} activos</div>
          </div>
          <div className="card">
            <h3>Suscriptores referidos</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{totales.subs_referidos}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>activos hoy</div>
          </div>
          <div className="card">
            <h3>Ingreso generado</h3>
            <div className="stat" style={{ color: '#10B981' }}>{fmtUYU(totales.ingreso)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>todos los pagos cobrados</div>
          </div>
          <div className="card">
            <h3>Comisiones pendientes</h3>
            <div className="stat" style={{ color: '#f59e0b' }}>{fmtUYU(totales.comision_pendiente)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>por transferir</div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>🏢</div>
            <h3>Sin vecinos en el programa</h3>
            <p>Cuando registres porteros van a aparecer acá con sus métricas.</p>
          </div>
        )}

        {!loading && filas.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Vecino</th>
                  <th>Edificio / Lugar</th>
                  <th style={{ textAlign: 'center' }}>Suscrip.<br/><small style={{ fontWeight: 400 }}>activos / unidades</small></th>
                  <th style={{ textAlign: 'right' }}>Ingreso</th>
                  <th style={{ textAlign: 'right' }}>Pendiente</th>
                  <th style={{ textAlign: 'center' }}>🛡️ Riesgo</th>
                </tr>
              </thead>
              <tbody>
                {filas.map(r => {
                  const riesgoColor = r.riesgo >= 50 ? { bg: '#fee2e2', color: '#991b1b', label: '🚨 Alto' }
                                    : r.riesgo >= 25 ? { bg: '#fef3c7', color: '#92400e', label: '⚠️ Medio' }
                                    : { bg: '#d1fae5', color: '#065f46', label: '✓ OK' }
                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 700, color: '#1a3461' }}>{r.nombre}</div>
                        {r.phone && <div style={{ fontSize: 11, color: '#94a3b8' }}>📱 {r.phone}</div>}
                        {!r.activo && <span style={{ fontSize: 10, padding: '1px 6px', background: '#f1f5f9', color: '#64748b', borderRadius: 4 }}>inactivo</span>}
                      </td>
                      <td style={{ fontSize: 13, color: '#475569' }}>
                        {r.lugar || '—'}
                        {r.barrio && <div style={{ fontSize: 11, color: '#94a3b8' }}>🗺️ {r.barrio}</div>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>
                          {r.subs_activos}{r.cant_unidades ? ` / ${r.cant_unidades}` : ''}
                        </div>
                        {r.penetracion_pct !== null && (
                          <div style={{ fontSize: 10,
                            color: r.penetracion_pct > 90 ? '#991b1b' : r.penetracion_pct > 70 ? '#92400e' : '#94a3b8',
                            fontWeight: 700,
                          }}>
                            {r.penetracion_pct.toFixed(0)}% penetración
                          </div>
                        )}
                        {r.subs_cancelados > 0 && (
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.subs_cancelados} cancel.</div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#10B981' }}>{fmtUYU(r.ingreso_generado)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.comision_pendiente > 0 ? '#f59e0b' : '#cbd5e1', fontWeight: 700 }}>
                        {r.comision_pendiente > 0 ? fmtUYU(r.comision_pendiente) : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                          background: riesgoColor.bg, color: riesgoColor.color,
                        }}>
                          {riesgoColor.label} ({r.riesgo})
                        </span>
                        {r.motivos_riesgo.length > 0 && (
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, maxWidth: 200 }}>
                            {r.motivos_riesgo[0]}
                            {r.motivos_riesgo.length > 1 && ` (+${r.motivos_riesgo.length - 1})`}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Comisiones en hold (verificación primer cobro) */}
        <SeccionHolds comisiones={comisiones} porteros={porteros} onCambio={cargar} />

        <AnalisisIA
          reportType="vecinos"
          data={{
            totales,
            vecinos: filas.slice(0, 20).map(r => ({
              nombre: r.nombre,
              edificio: r.edificio_nombre || r.direccion,
              barrio: r.barrio,
              cant_unidades: r.cant_unidades,
              activo: r.activo,
              suscriptores_activos: r.subs_activos,
              suscriptores_cancelados: r.subs_cancelados,
              ingreso_generado: r.ingreso_generado,
              comision_pendiente: r.comision_pendiente,
              penetracion_pct: r.penetracion_pct?.toFixed(1),
              match_direccion_pct: r.match_direccion_pct?.toFixed(0),
              match_barrio_pct: r.match_barrio_pct?.toFixed(0),
              cancel_pct: r.cancel_pct?.toFixed(0),
              riesgo_score: r.riesgo,
              motivos_riesgo: r.motivos_riesgo,
            })),
          }}
          titulo="🛡️ Salud y detección de fraude — Análisis IA"
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> El programa de referidos es uno de los principales canales de crecimiento.
          Identificá los top performers y ofrecéles incentivos extra. Las comisiones pendientes se administran en /admin/cobros.
        </div>
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th { text-align: left; padding: 12px 14px; background: #f8fafc; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-transform: uppercase; color: #6b7280; font-weight: 700; letter-spacing: .03em; }
        .usu-table tbody td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }
      `}</style>
    </AdminLayout>
  )
}

function SeccionHolds({ comisiones, porteros, onCambio }) {
  const holds = useMemo(() => {
    const porteroMap = new Map(porteros.map(p => [p.id, p]))
    return comisiones
      .filter(c => c.estado === 'hold_verificacion')
      .map(c => ({ ...c, portero: porteroMap.get(c.portero_id) }))
      .sort((a, b) => new Date(a.hold_hasta || 0) - new Date(b.hold_hasta || 0))
  }, [comisiones, porteros])

  if (holds.length === 0) return null

  async function liberar(comision_id) {
    if (!confirm('¿Liberar esta comisión? Va a pasar a "pendiente" y se podrá pagar en la próxima liquidación.')) return
    try {
      const { error } = await supabase.rpc('liberar_hold_comision', { p_comision_id: comision_id })
      if (error) throw error
      onCambio()
    } catch (e) { alert('Error: ' + e.message) }
  }

  const totalHold = holds.reduce((s, c) => s + Number(c.monto_total), 0)

  return (
    <div style={{ marginTop: 24, marginBottom: 16, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: '#3730a3', fontSize: 16 }}>🔒 Comisiones en hold de verificación ({holds.length})</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#4338ca' }}>
          Primeras comisiones de porteros nuevos. Quedan retenidas 30 días para que verifiques identidad.
          Total retenido: <strong>${Math.round(totalHold).toLocaleString('es-UY')}</strong>
        </p>
      </div>
      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Portero</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Mes</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Monto</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Liberación auto</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {holds.map(h => {
              const dias = h.hold_hasta ? Math.max(0, Math.ceil((new Date(h.hold_hasta) - new Date()) / (1000 * 60 * 60 * 24))) : null
              return (
                <tr key={h.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <strong>{h.portero?.profile?.nombre || '—'}</strong>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{h.portero?.lugar}</div>
                  </td>
                  <td style={{ padding: '10px 12px', textTransform: 'capitalize' }}>
                    {h.mes ? new Date(h.mes + 'T12:00:00').toLocaleDateString('es-UY', { month: 'long', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#3730a3' }}>${Number(h.monto_total).toLocaleString('es-UY')}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12 }}>
                    {h.hold_hasta ? new Date(h.hold_hasta).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' }) : '—'}
                    {dias !== null && <div style={{ fontSize: 10, color: dias < 7 ? '#92400e' : '#94a3b8' }}>en {dias} día{dias !== 1 ? 's' : ''}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <button onClick={() => liberar(h.id)}
                      style={{ background: '#10B981', color: '#fff', border: 0, borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      ✓ Liberar ya
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#4338ca', lineHeight: 1.5 }}>
        💡 <strong>Cómo funciona:</strong> Si ya verificaste al portero (visita, CI, etc) podés liberar manualmente.
        Si no hacés nada, se libera automáticamente cuando vence (cron diario).
      </div>
    </div>
  )
}
